import {
    Stack,
    Text,
    Input,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Button
} from '@chakra-ui/react'
import * as yup from 'yup'
import CustomLink from '@/components/CustomLink'
import { useForm, Controller } from 'react-hook-form'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/context/AuthContext'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo } from 'react'

const DEFAULT_RETURN_URL = '/user/dashboard'

const loginSchema = yup
    .object({
        email: yup.string().email().required(),
        password: yup
            .string()
            .required('Password is required')
            .min(8, 'Password must be at least 8 characters')
    })
    .required()

type LoginFormData = yup.InferType<typeof loginSchema>

export default function LoginForm() {
    const { isAuthenticated, login, isLoading } = useAuth()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    // memoize so the effect doesn't loop on every render (searchParams is a new object each render)
    const returnUrl = useMemo(
        () => searchParams.get('returnUrl') ?? DEFAULT_RETURN_URL,
        [searchParams]
    )

    const {
        register,
        handleSubmit,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(loginSchema),
        defaultValues: {
            email: '',
            password: ''
        }
    })

    useEffect(() => {
        if (isAuthenticated) {
            navigate(returnUrl, { replace: true })
        }
    }, [isAuthenticated, navigate, returnUrl])

    const handleLoginSubmit = (data: LoginFormData) => {
        login(data.email, data.password)
    }

    return (
        <Stack spacing={4}>
            <FormControl isInvalid={Boolean(errors.email)} isRequired>
                <FormLabel>Email address</FormLabel>
                <Input type="email" id="email" {...register('email')} />
                <FormErrorMessage>
                    {errors.email ? errors.email.message : null}
                </FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={Boolean(errors.password)} isRequired>
                <FormLabel>Password</FormLabel>
                <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                        <Input type="password" id="password" {...field} />
                    )}
                />
                <FormErrorMessage>
                    {errors.password ? errors.password.message : null}
                </FormErrorMessage>
            </FormControl>

            <Stack spacing={4} pt={2}>
                <Button
                    type="submit"
                    loadingText="Submitting"
                    size="lg"
                    isLoading={isLoading}
                    onClick={handleSubmit(handleLoginSubmit)}
                >
                    Login
                </Button>
                <Text align={'center'}>
                    Forgot Password?{' '}
                    <CustomLink href="/account/reset">Reset here</CustomLink>
                </Text>
            </Stack>
        </Stack>
    )
}
